import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import SettingsModal from './SettingsModal';
import AccountDropdown from './AccountDropdown';
import { Button } from '@/components/ui/button';
import logoImage from '@/assets/images/logo.png';

interface LayoutProps {
  children?: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const location = useLocation();
  
  // Debug when the Layout component renders
  useEffect(() => {
    console.log('Layout rendered with path:', location.pathname, 'search:', location.search);
  }, [location]);
  
  // Check if we're on the main page
  const isMainPage = location.pathname === '/';
  
  // Force a unique key based on the full URL + a timestamp to ensure remounting
  const outletKey = `${location.pathname}${location.search}-${Date.now()}`;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <nav className="bg-card border-b-2 border-indigo-400 dark:border-indigo-500 shadow-sm">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex justify-between h-28 py-2">
            <div className="flex items-center space-x-2">
              {!isMainPage && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="mr-2 rounded-full hover:bg-indigo-50 dark:hover:bg-indigo-950"
                  asChild
                >
                  <Link to="/">
                    <ChevronLeft className="h-5 w-5" />
                    <span className="sr-only">Back to dashboard</span>
                  </Link>
                </Button>
              )}
              <Link to="/" className="flex items-center group">
                <div className="h-24 w-24 mr-3 rounded-full border-2 border-indigo-400 dark:border-indigo-500 shadow-sm overflow-hidden flex items-center justify-center bg-white dark:bg-gray-900">
                  <img 
                    src={logoImage} 
                    alt="Fore-Poster Logo" 
                    className="h-28 w-28 object-cover transform scale-100 object-center"
                  />
                </div>
                <span className="text-2xl font-bold text-foreground group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                  foreposter
                </span>
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              {user && (
                <AccountDropdown onSettingsClick={() => setIsSettingsOpen(true)} />
              )}
            </div>
          </div>
        </div>
      </nav>

      <main className="flex-grow">
        {/* Note: Always forcing a re-render of children with a new timestamp */}
        {children ?? <Outlet key={outletKey} />}
      </main>

      <footer className="bg-card border-t border-indigo-200 dark:border-indigo-800 py-4 mt-auto">
        <div className="max-w-7xl mx-auto px-8 text-sm text-center text-muted-foreground">
          <p>
            Vibe coded by <a href="https://www.cjsymonds.com/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline">Christopher Symonds</a>
            {' • '}
            Copyright © {new Date().getFullYear()}{' '}
            <a href="https://www.visionstudioshub.com/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline">Vision Studios LLC</a>
          </p>
        </div>
      </footer>

      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
    </div>
  );
};

export default Layout;